import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_AUTH_API_URL || 'http://localhost:8000/api';

// Create axios instance for auth API calls (with interceptors)
const authAPI = axios.create({
  baseURL: `${API_BASE_URL}/auth`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate axios instance for login (without 401 interceptor)
const loginAPI = axios.create({
  baseURL: `${API_BASE_URL}/auth`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
authAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
authAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      authService.logout();
    }
    // Handle network errors (like CORS) - treat as authentication issue
    else if (!error.response && error.code === 'ERR_NETWORK') {
      authService.logout();
    }

    return Promise.reject(error);
  }
);

export const authService = {
  // Login with username and password
  login: async (username, password) => {
    const response = await loginAPI.post('/login', {
      username,
      password,
    });

    const { access_token, user } = response.data;

    // MUTUAL EXCLUSION: Clear any existing student session
    localStorage.removeItem('studentAuthToken');
    localStorage.removeItem('studentUser');
    localStorage.removeItem('mustChangePassword');

    // Stop any existing token validation interval
    authService.stopTokenValidation();

    // Store admin token and user info
    localStorage.setItem('authToken', access_token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('userType', 'admin');

    // Start periodic token validation
    authService.startTokenValidation();

    return response.data;
  },

  // Logout function
  logout: () => {
    // Prevent multiple logout calls
    if (authService._isLoggingOut) return;
    authService._isLoggingOut = true;

    // Stop token validation
    authService.stopTokenValidation();

    // Clear admin session
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');

    // Show toast if available
    if (typeof window !== 'undefined' && window.showToast) {
      window.showToast('Session expired. Please login again.', 'warning');
    }

    // Redirect to login page
    window.location.href = '/login';
  },

  // Get current user info
  getCurrentUser: async () => {
    const response = await authAPI.get('/me');
    return response.data;
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem('authToken');
    const userType = localStorage.getItem('userType');

    // Must have token AND be admin userType
    if (!token || userType !== 'admin') return false;

    // Check if token is expired (JWT tokens have expiration)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;

      if (payload.exp && payload.exp < currentTime) {
        // Don't call logout here to avoid redirect loops
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userType');
        return false;
      }

      return true;
    } catch (error) {
      // Invalid token format
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('userType');
      return false;
    }
  },

  // Get stored user info
  getUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Get auth token
  getToken: () => {
    return localStorage.getItem('authToken');
  },

  // Setup admin user (first time setup)
  setupAdmin: async () => {
    const response = await authAPI.post('/setup-admin');
    return response.data;
  },

  // Register new user (admin only)
  registerUser: async (userData) => {
    const response = await authAPI.post('/register', userData);
    return response.data;
  },

  // Get all users (admin only)
  getUsers: async () => {
    const response = await authAPI.get('/users');
    return response.data;
  },

  // Get organization users (for task assignment - all authenticated users)
  getOrganizationUsers: async () => {
    const response = await authAPI.get('/users/organization');
    return response.data;
  },

  // Start periodic token validation
  startTokenValidation: () => {
    // Clear existing interval first to prevent leaks
    authService.stopTokenValidation();

    // Check token every 5 minutes
    const interval = setInterval(() => {
      if (!authService.isAuthenticated()) {
        clearInterval(interval);
      }
    }, 5 * 60 * 1000); // 5 minutes

    // Store interval ID for cleanup
    authService.tokenValidationInterval = interval;
  },

  // Stop periodic token validation
  stopTokenValidation: () => {
    if (authService.tokenValidationInterval) {
      clearInterval(authService.tokenValidationInterval);
      authService.tokenValidationInterval = null;
    }
  },

  // Internal flag to prevent multiple logout calls
  _isLoggingOut: false,
};

// Listen for storage changes from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    // If authToken was removed in another tab, logout this tab too
    if (event.key === 'authToken' && event.newValue === null) {
      const currentUserType = localStorage.getItem('userType');
      // Only react if we were an admin session
      if (currentUserType === 'admin' || currentUserType === null) {
        authService.stopTokenValidation();
        window.location.href = '/login';
      }
    }
  });
}

export default authService; 